import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { fileUrl, humanSize } from "../lib/apiExtra";
import { useAuth } from "../context/AuthContext";
import JitsiRoom from "../components/JitsiRoom";
import { toast } from "sonner";
import { Send, Paperclip, FileText, Loader2, Video, Search, PhoneOff, ShieldCheck, User } from "lucide-react";

export default function DirectMessages() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [unread, setUnread] = useState({});
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [inCall, setInCall] = useState(false);
  const [q, setQ] = useState("");
  const lastRef = useRef(null);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const loadContacts = async () => {
    try { const { data } = await api.get("/dm/contacts"); setContacts(data); } catch {}
  };
  const loadUnread = async () => {
    try { const { data } = await api.get("/dm/meta/unread"); setUnread(data.contacts || {}); } catch {}
  };
  useEffect(() => { loadContacts(); loadUnread(); const t = setInterval(loadUnread, 5000); return () => clearInterval(t); }, []);

  const loadMsgs = async () => {
    if (!active) return;
    try {
      const after = lastRef.current;
      const { data } = await api.get(`/dm/${active.id}` + (after ? `?after=${encodeURIComponent(after)}` : ""));
      if (data.length) {
        setMessages((prev) => { const seen = new Set(prev.map((m) => m.id)); return [...prev, ...data.filter((m) => !seen.has(m.id))]; });
        lastRef.current = data[data.length - 1].created_at;
      }
      loadUnread();
    } catch {}
  };
  useEffect(() => {
    setMessages([]); lastRef.current = null; setInCall(false);
    if (!active) return;
    loadMsgs();
    const t = setInterval(loadMsgs, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [active?.id]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!active || (!text.trim() && !file)) return;
    setSending(true);
    try {
      const fd = new FormData(); fd.append("text", text); if (file) fd.append("file", file);
      await api.post(`/dm/${active.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText(""); setFile(null); if (fileRef.current) fileRef.current.value = "";
      await loadMsgs();
    } catch { toast.error("Could not send"); }
    finally { setSending(false); }
  };

  const roomName = active ? `titlehub-dm-${[user.id, active.id].sort().join("-")}` : "";
  const fmt = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const shown = contacts.filter((c) =>
    (c.name || "").toLowerCase().includes(q.toLowerCase()) || (c.email || "").toLowerCase().includes(q.toLowerCase()));
  const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-700 tracking-tight text-slate-100">Communication Hub</h1>
        <p className="text-sm text-slate-400 mt-1">Private 1-to-1 messages with any teammate — share files, start a call and screen-share. Order-specific threads live on each order page.</p>
      </div>

      <div className="grid grid-cols-12 gap-4" style={{ minHeight: "70vh" }}>
        {/* Contacts */}
        <div className="col-span-12 lg:col-span-4 rounded-lg border border-border bg-card overflow-hidden flex flex-col">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="dm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search teammates…"
                className="w-full rounded-md border border-white/15 pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {shown.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No teammates found.</div>}
            {shown.map((c) => (
              <button key={c.id} data-testid={`dm-contact-${c.id}`} onClick={() => setActive(c)}
                className={`w-full flex items-center gap-3 px-3 py-3 text-left border-b border-white/10 transition-colors ${active?.id === c.id ? "bg-accent" : "hover:bg-white/5"}`}>
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-slate-300 text-xs font-bold shrink-0">{initials(c.name)}</div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-100 truncate flex items-center gap-1">
                    {c.name}
                    {c.role === "admin" && <ShieldCheck className="h-3 w-3 text-blue-500" />}
                  </div>
                  <div className="text-xs text-slate-400 font-mono truncate">{c.email}</div>
                </div>
                {unread[c.id] > 0 && (
                  <span data-testid={`dm-unread-${c.id}`} className="inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold h-5 min-w-[20px] px-1">{unread[c.id]}</span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Conversation */}
        <div className="col-span-12 lg:col-span-8 rounded-lg border border-border bg-card overflow-hidden flex flex-col">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-400" data-testid="dm-empty">
              Select a teammate to start a private conversation.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-slate-300 text-xs font-bold">{initials(active.name)}</div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-100 truncate" data-testid="dm-active-name">{active.name}</div>
                    <div className="text-xs text-slate-400 font-mono truncate">{active.email}</div>
                  </div>
                </div>
                <button data-testid="dm-call-btn" onClick={() => setInCall((v) => !v)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${inCall ? "bg-red-600 text-white hover:bg-red-700" : "bg-primary text-white hover:bg-navy-2"}`}>
                  {inCall ? <><PhoneOff className="h-3.5 w-3.5" /> End call</> : <><Video className="h-3.5 w-3.5" /> Call / Screen-share</>}
                </button>
              </div>

              {inCall && (
                <div className="p-3 border-b border-border">
                  <JitsiRoom room={roomName} height="48vh" />
                  <p className="text-[11px] text-slate-400 mt-1">Use the screen-share button inside the call to share your screen for clarification.</p>
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="dm-messages">
                {messages.length === 0 && <div className="text-center text-sm text-slate-400 mt-6">No messages yet. Say hello 👋</div>}
                {messages.map((m) => {
                  const mine = m.sender_id === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${mine ? "bg-primary text-white" : "bg-white/10 text-slate-100"}`}>
                        {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                        {m.attachment && (
                          <a href={fileUrl(m.attachment.url)} target="_blank" rel="noopener noreferrer"
                            className={`mt-1 flex items-center gap-2 rounded-md border px-2 py-1 text-xs ${mine ? "border-white/40 text-white" : "border-white/15 text-slate-300 bg-card"}`}>
                            <FileText className="h-3.5 w-3.5" /> {m.attachment.name}
                            <span className="opacity-70">{humanSize(m.attachment.size)}</span>
                          </a>
                        )}
                        <div className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-slate-400"}`}>{fmt(m.created_at)}</div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>

              <form onSubmit={send} className="border-t border-border p-3 flex items-center gap-2">
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="dm-attach-btn"
                  className="rounded-md border border-white/15 p-2 text-slate-400 hover:bg-white/5 transition-colors">
                  <Paperclip className="h-4 w-4" />
                </button>
                <input ref={fileRef} type="file" className="hidden" data-testid="dm-file-input" onChange={(e) => setFile(e.target.files[0])} />
                <input data-testid="dm-input" value={text} onChange={(e) => setText(e.target.value)}
                  placeholder={file ? `Attach: ${file.name}` : "Type a message…"}
                  className="flex-1 rounded-md border border-white/15 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none" />
                <button type="submit" data-testid="dm-send-btn" disabled={sending}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-navy-2 transition-colors disabled:opacity-50">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
